import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  List,
  MessageSquare,
  Link2,
  FileText,
  Settings,
  XCircle,
  ClipboardList,
  AlertTriangle,
  Info,
} from "lucide-react";
import PopUp from "./PopUp";
import { useTranslation } from "../hooks/useTranslation";

const FeedbackModal = ({ open, onClose, jobId, token, apiBaseUrl, onFeedbackSubmitted }) => {
  const { t } = useTranslation("common");
  const [feedbackType, setFeedbackType] = useState(null);
  const [selectedPositive, setSelectedPositive] = useState([]);
  const [selectedNegative, setSelectedNegative] = useState([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasFeedback, setHasFeedback] = useState(false);

  const positiveOptions = useMemo(
    () => [
      { key: "teses", label: t("review.feedback.positiveOptions.teses"), Icon: BookOpen },
      { key: "estrutura", label: t("review.feedback.positiveOptions.estrutura"), Icon: List },
      { key: "argumentacao", label: t("review.feedback.positiveOptions.argumentacao"), Icon: MessageSquare },
      { key: "referencias", label: t("review.feedback.positiveOptions.referencias"), Icon: Link2 },
      { key: "formatacao", label: t("review.feedback.positiveOptions.formatacao"), Icon: FileText },
      { key: "parametros", label: t("review.feedback.positiveOptions.parametros"), Icon: Settings },
    ],
    [t]
  );

  const negativeOptions = useMemo(
    () => [
      { key: "teses_faltando", label: t("review.feedback.negativeOptions.teses_faltando"), Icon: BookOpen },
      { key: "teses_erradas", label: t("review.feedback.negativeOptions.teses_erradas"), Icon: XCircle },
      { key: "estrutura", label: t("review.feedback.negativeOptions.estrutura"), Icon: List },
      { key: "referencias", label: t("review.feedback.negativeOptions.referencias"), Icon: Link2 },
      { key: "formatacao", label: t("review.feedback.negativeOptions.formatacao"), Icon: FileText },
      { key: "parametros", label: t("review.feedback.negativeOptions.parametros"), Icon: Settings },
      { key: "regras", label: t("review.feedback.negativeOptions.regras"), Icon: ClipboardList },
      { key: "jurisprudencia", label: t("review.feedback.negativeOptions.jurisprudencia"), Icon: AlertTriangle },
    ],
    [t]
  );

  const needsComment =
    feedbackType === "NEGATIVE" &&
    (selectedNegative.includes("jurisprudencia") ||
      selectedNegative.includes("teses_erradas"));

  const canSubmit =
    !!feedbackType &&
    (feedbackType === "POSITIVE" ||
      (feedbackType === "NEGATIVE" &&
        (selectedNegative.length > 0 || comment.trim().length > 0)));

  const checkExistingFeedback = useCallback(async () => {
    try {
      const url = `${apiBaseUrl}/doc_creator/review/feedback/${jobId}/user`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_feedback) {
          setHasFeedback(true);
          if (data.feedback) {
            setFeedbackType(data.feedback.feedback_type);
            setComment(data.feedback.comment || "");
            const extra = data.feedback.extra_data || {};
            setSelectedPositive(extra.selected_positive || extra.selectedPositive || []);
            setSelectedNegative(extra.selected_negative || extra.selectedNegative || []);
          }
        }
      }
    } catch (err) {
      console.error("Erro ao verificar feedback existente:", err);
    }
  }, [apiBaseUrl, jobId, token]);

  useEffect(() => {
    if (open && jobId) {
      checkExistingFeedback();
    }
  }, [open, jobId, checkExistingFeedback]);

  useEffect(() => {
    if (!open) {
      setFeedbackType(null);
      setSelectedPositive([]);
      setSelectedNegative([]);
      setComment("");
      setError(null);
      setHasFeedback(false);
    }
  }, [open]);

  const togglePositive = (key) => {
    setSelectedPositive((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleNegative = (key) => {
    setSelectedNegative((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    if (!feedbackType) {
      setError(t("review.feedback.selectType"));
      return;
    }

    if (needsComment && (!comment || !comment.trim())) {
      setError(t("review.feedback.describeProblem"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const url = `${apiBaseUrl}/doc_creator/review/feedback/${jobId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          feedback_type: feedbackType,
          comment: comment.trim() || null,
          extra_data: {
            ui: "feedback-modal-elaw",
            selected_positive: selectedPositive,
            selected_negative: selectedNegative,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || errorData?.message || t("review.feedback.error")
        );
      }

      const data = await response.json();
      console.log("✅ Feedback enviado com sucesso:", data);

      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(data);
      }

      onClose();
    } catch (err) {
      console.error("❌ Erro ao enviar feedback:", err);
      setError(err.message || t("review.feedback.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const disabledState = isSubmitting || hasFeedback;

  return (
    <PopUp
      open={open}
      onClose={handleClose}
      title={hasFeedback ? t("review.feedback.titleExisting") : t("review.feedback.title")}
    >
      <div style={{ minWidth: "400px", maxWidth: "600px" }}>
        {hasFeedback && (
          <div className="ikaro-alert ikaro-alert-info mb-16">
            {t("review.feedback.alreadySubmitted")}
          </div>
        )}

        {error && (
          <div className="ikaro-alert ikaro-alert-warning mb-16">
            {error}
          </div>
        )}

        <div className="flex-row gap-8 mb-20">
          <button
            type="button"
            disabled={disabledState}
            onClick={() => {
              setFeedbackType("POSITIVE");
              setSelectedNegative([]);
            }}
            className="feedback-type-btn"
            style={{
              backgroundColor:
                feedbackType === "POSITIVE"
                  ? "var(--ikaro-orange-primary)"
                  : "var(--ikaro-gray-light)",
              color: feedbackType === "POSITIVE" ? "white" : "var(--ikaro-gray-dark)",
              opacity: disabledState ? 0.6 : 1,
              cursor: disabledState ? "not-allowed" : "pointer",
            }}
          >
            <ThumbsUp size={18} />
            {t("review.feedback.positiveButton")}
          </button>
          <button
            type="button"
            disabled={disabledState}
            onClick={() => {
              setFeedbackType("NEGATIVE");
              setSelectedPositive([]);
            }}
            className="feedback-type-btn"
            style={{
              backgroundColor:
                feedbackType === "NEGATIVE"
                  ? "var(--ikaro-status-warning)"
                  : "var(--ikaro-gray-light)",
              color: feedbackType === "NEGATIVE" ? "white" : "var(--ikaro-gray-dark)",
              opacity: disabledState ? 0.6 : 1,
              cursor: disabledState ? "not-allowed" : "pointer",
            }}
          >
            <ThumbsDown size={18} />
            {t("review.feedback.negativeButton")}
          </button>
        </div>

        {feedbackType === "POSITIVE" && (
          <div className="mb-20">
            <div className="ikaro-section-label">
              {t("review.feedback.whatWorked")} <span style={{ fontWeight: 400 }}>({t("review.feedback.optional")})</span>
            </div>
            <div className="flex-row flex-wrap gap-8 mb-12">
              {positiveOptions.map(({ key, label, Icon }) => {
                const selected = selectedPositive.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={disabledState}
                    onClick={() => togglePositive(key)}
                    className="feedback-chip"
                    style={{
                      backgroundColor: selected ? "var(--ikaro-orange-primary)" : "var(--ikaro-gray-light)",
                      color: selected ? "white" : "var(--ikaro-gray-dark)",
                      opacity: disabledState ? 0.6 : 1,
                      cursor: disabledState ? "not-allowed" : "pointer",
                    }}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={disabledState}
              placeholder={t("review.feedback.commentPlaceholderPositive")}
              rows={2}
              className="ikaro-textarea"
              style={{ opacity: disabledState ? 0.6 : 1 }}
            />
          </div>
        )}

        {feedbackType === "NEGATIVE" && (
          <div className="mb-20">
            <div className="ikaro-section-label">
              {t("review.feedback.whatWentWrong")} <span style={{ color: "var(--ikaro-status-warning)" }}>{t("review.feedback.required")}</span>
            </div>
            <div className="flex-row flex-wrap gap-8 mb-12">
              {negativeOptions.map(({ key, label, Icon }) => {
                const selected = selectedNegative.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={disabledState}
                    onClick={() => toggleNegative(key)}
                    className="feedback-chip"
                    style={{
                      backgroundColor: selected ? "var(--ikaro-status-warning)" : "var(--ikaro-gray-light)",
                      color: selected ? "white" : "var(--ikaro-gray-dark)",
                      opacity: disabledState ? 0.6 : 1,
                      cursor: disabledState ? "not-allowed" : "pointer",
                    }}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                );
              })}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={disabledState}
              placeholder={
                needsComment
                  ? t("review.feedback.commentPlaceholderNegativeRequired")
                  : t("review.feedback.commentPlaceholderNegativeOptional")
              }
              rows={2}
              className="ikaro-textarea"
              style={{
                borderColor: needsComment && !comment.trim()
                  ? "var(--ikaro-status-warning)"
                  : undefined,
                opacity: disabledState ? 0.6 : 1,
              }}
            />
            {needsComment && (
              <div className="flex-row gap-6 mt-4 text-sm" style={{ color: "var(--ikaro-status-warning-text)" }}>
                <Info size={14} />
                {t("review.feedback.describeToInvestigate")}
              </div>
            )}
          </div>
        )}

        <div className="flex-row-end gap-12 mt-24">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="ikaro-btn ikaro-btn-md ikaro-btn-secondary"
            style={{ opacity: isSubmitting ? 0.6 : 1 }}
          >
            {hasFeedback ? t("review.feedback.close") : t("review.feedback.skip")}
          </button>
          {!hasFeedback && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting || !canSubmit || (needsComment && !comment.trim())
              }
              className="ikaro-btn ikaro-btn-md ikaro-btn-primary"
            >
              {isSubmitting ? t("review.feedback.submitting") : t("review.feedback.submit")}
            </button>
          )}
        </div>
      </div>
    </PopUp>
  );
};

export default FeedbackModal;
